import { provideZoneChangeDetection, isDevMode } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app.config';
import { AppComponent } from './app.component';
import { provideServiceWorker } from '@angular/service-worker';
import { isTauri, isPackagedApp } from '@shared/utils/platform.utils';
import { environment } from './environments/environment';

/**
 * Auto-updater de Tauri, BLOQUEANTE al arranque: si hay versión nueva la instala y relanza
 * antes de montar Angular (si la app abrió, está actualizada). Con timeout y tolerancia a
 * fallos: sin red o con el endpoint caído se arranca igual con la versión embebida.
 *
 * Se desactiva con `environment.desktopUpdaterEnabled` (off por defecto en desarrollo): en
 * `tauri:dev` el binario lleva la versión de `tauri.conf.json`, así que cualquier versión mayor
 * publicada en R2 interrumpía la sesión de trabajo instalándose y relanzando la app. Se puede
 * invertir por entorno con `NG_APP_DESKTOP_UPDATER` (ver `docs/modules/DESKTOP.md`).
 */
async function updateIfTauri(): Promise<boolean> {
  if (!isTauri()) return false;
  if (!environment.desktopUpdaterEnabled) {
    console.info('Updater: desactivado por configuración (NG_APP_DESKTOP_UPDATER).');
    return false;
  }
  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check({ timeout: 10_000 });
    if (!update) return false;

    document.body.innerHTML =
      '<div style="display:grid;place-items:center;height:100vh;font-family:system-ui">' +
      '<p>Actualizando Kerep a la versión ' +
      update.version +
      '…</p></div>';
    await update.downloadAndInstall();

    const { relaunch } = await import('@tauri-apps/plugin-process');
    await relaunch();
    return true; // no montar Angular: la app se está relanzando
  } catch (err) {
    console.warn('Updater: no se pudo comprobar/instalar; se continúa con la versión embebida.', err);
    return false;
  }
}

void updateIfTauri().then((relaunching) => {
  if (relaunching) return;
  bootstrapApplication(AppComponent, {
    ...appConfig,
    providers: [
      provideZoneChangeDetection(),
      ...appConfig.providers,
      provideServiceWorker('ngsw-worker.js', {
        // En apps empaquetadas (Tauri/Capacitor) el frontend va embebido y se actualiza
        // por su propio updater: sin SW.
        enabled: !isDevMode() && !isPackagedApp(),
        registrationStrategy: 'registerWhenStable:40000',
      }),
    ],
  });
});
